import type { PageDimensionsMm } from './geometry';
import { COMPOSITION_LOGO_MIN_NORMALIZED_SIZE } from './logo';
import type {
  CompositionDraft,
  CompositionLogo,
  CompositionTypography,
  NormalizedPoint,
  NormalizedRect,
} from './model';
import {
  COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM,
  COMPOSITION_LEGEND_SPACING_MAX_MM,
  COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM,
  COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM,
  COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT,
  COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT,
  COMPOSITION_TYPOGRAPHY_MIN_PT,
} from './model';

export interface PageResizeFactors {
  readonly rx: number;
  readonly ry: number;
  readonly scalar: number;
}

export function pageResizeFactors(
  oldPage: PageDimensionsMm,
  newPage: PageDimensionsMm,
): PageResizeFactors | null {
  if (
    ![oldPage.width, oldPage.height, newPage.width, newPage.height].every(
      (value) => Number.isFinite(value) && value > 0,
    )
  )
    return null;
  const rx = newPage.width / oldPage.width;
  const ry = newPage.height / oldPage.height;
  const scalar = Math.sqrt(rx * ry);
  return [rx, ry, scalar].every((value) => Number.isFinite(value) && value > 0)
    ? { rx, ry, scalar }
    : null;
}

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

function retainPhysicalPoint(
  point: NormalizedPoint,
  factors: PageResizeFactors,
  maximumY = 0.98,
): NormalizedPoint {
  return {
    x: clamp(point.x / factors.rx, 0, 0.98),
    y: clamp(point.y / factors.ry, 0, maximumY),
  };
}

function retainPhysicalRect(rect: NormalizedRect, factors: PageResizeFactors): NormalizedRect {
  const x = clamp(rect.x / factors.rx, 0, 1);
  const y = clamp(rect.y / factors.ry, 0, 1);
  const width = clamp(rect.width / factors.rx, 0.05, 1);
  const height = clamp(rect.height / factors.ry, 0.05, 1);
  return {
    x: Math.min(x, 1 - width),
    y: Math.min(y, 1 - height),
    width,
    height,
  };
}

function scaleTypography(
  typography: CompositionTypography,
  scalar: number,
  minimum = COMPOSITION_TYPOGRAPHY_MIN_PT,
  maximum = 96,
): CompositionTypography {
  return {
    ...typography,
    fontSizePt: clamp(typography.fontSizePt * scalar, minimum, maximum),
  };
}

function resizedLogo(
  logo: CompositionLogo | null,
  oldPage: PageDimensionsMm,
  newPage: PageDimensionsMm,
  factors: PageResizeFactors,
  scaleLayout: boolean,
): CompositionLogo | null {
  if (!logo) return null;
  const old = logo.bounds;
  // Preserve the authored frame's physical ratio and page-relative centre when scaling.
  // The independently contained image retains its intrinsic ratio in every renderer.
  const widthMm = old.width * oldPage.width * (scaleLayout ? factors.scalar : 1);
  const heightMm = (widthMm * (old.height * oldPage.height)) / (old.width * oldPage.width);
  const width = widthMm / newPage.width;
  const height = heightMm / newPage.height;
  const fit = clamp(
    1,
    Math.max(
      COMPOSITION_LOGO_MIN_NORMALIZED_SIZE / width,
      COMPOSITION_LOGO_MIN_NORMALIZED_SIZE / height,
    ),
    Math.min(1 / width, 1 / height),
  );
  const fittedWidth = width * fit;
  const fittedHeight = height * fit;
  const centreX = scaleLayout ? old.x + old.width / 2 : (old.x + old.width / 2) / factors.rx;
  const centreY = scaleLayout ? old.y + old.height / 2 : (old.y + old.height / 2) / factors.ry;
  return {
    ...logo,
    bounds: {
      x: clamp(centreX - fittedWidth / 2, 0, 1 - fittedWidth),
      y: clamp(centreY - fittedHeight / 2, 0, 1 - fittedHeight),
      width: fittedWidth,
      height: fittedHeight,
    },
  };
}

/** Transforms only authored Composer furniture. Source layers, annotations and camera stay untouched. */
export function resizeCompositionLayout(
  composition: CompositionDraft,
  oldPage: PageDimensionsMm,
  newPage: PageDimensionsMm,
  scaleLayout: boolean,
): CompositionDraft | null {
  const factors = pageResizeFactors(oldPage, newPage);
  if (!factors) return null;
  if (oldPage.width === newPage.width && oldPage.height === newPage.height) return composition;
  const { scalar } = factors;
  const point = (value: NormalizedPoint, maximumY?: number) =>
    scaleLayout ? value : retainPhysicalPoint(value, factors, maximumY);
  const rect = (value: NormalizedRect) =>
    scaleLayout ? value : retainPhysicalRect(value, factors);
  if (!scaleLayout) {
    return {
      ...composition,
      mapFrame: { ...composition.mapFrame, bounds: rect(composition.mapFrame.bounds) },
      title: { ...composition.title, position: point(composition.title.position) },
      subtitle: { ...composition.subtitle, position: point(composition.subtitle.position) },
      legend: { ...composition.legend, bounds: rect(composition.legend.bounds) },
      scaleBar: { ...composition.scaleBar, position: point(composition.scaleBar.position) },
      northArrow: { ...composition.northArrow, position: point(composition.northArrow.position) },
      sourcesAttribution: {
        ...composition.sourcesAttribution,
        ...(composition.sourcesAttribution.width !== undefined
          ? { width: clamp(composition.sourcesAttribution.width / factors.rx, 0.02, 1) }
          : {}),
        position: scaleLayout
          ? composition.sourcesAttribution.position
          : {
              x: clamp(composition.sourcesAttribution.position.x / factors.rx, 0, 1),
              y: clamp(composition.sourcesAttribution.position.y / factors.ry, 0, 1),
            },
      },
      logo: resizedLogo(composition.logo, oldPage, newPage, factors, false),
      insetMap: composition.insetMap
        ? { ...composition.insetMap, bounds: rect(composition.insetMap.bounds) }
        : null,
    };
  }
  const scaled = (value: number, minimum: number, maximum: number) =>
    clamp(value * scalar, minimum, maximum);
  const legend = composition.legend;
  return {
    ...composition,
    insetMap: composition.insetMap
      ? {
          ...composition.insetMap,
          border: {
            ...composition.insetMap.border,
            widthMm: scaled(composition.insetMap.border.widthMm, 0.1, 5),
          },
          cornerRadiusMm: scaled(composition.insetMap.cornerRadiusMm, 0, 50),
          indicator: {
            ...composition.insetMap.indicator,
            outlineWidthMm: scaled(composition.insetMap.indicator.outlineWidthMm, 0.1, 5),
          },
        }
      : null,
    mapFrame: {
      ...composition.mapFrame,
      ...(composition.mapFrame.cornerRadiusMm !== undefined
        ? { cornerRadiusMm: scaled(composition.mapFrame.cornerRadiusMm, 0, 50) }
        : {}),
      border: {
        ...composition.mapFrame.border,
        widthMm: scaled(composition.mapFrame.border.widthMm, 0.1, 5),
      },
    },
    title: {
      ...composition.title,
      typography: scaleTypography(composition.title.typography, scalar),
    },
    subtitle: {
      ...composition.subtitle,
      typography: scaleTypography(composition.subtitle.typography, scalar),
    },
    legend: {
      ...legend,
      border: { ...legend.border, widthMm: scaled(legend.border.widthMm, 0.1, 5) },
      cornerRadiusMm: scaled(legend.cornerRadiusMm, 0, COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM),
      paddingTopMm: scaled(legend.paddingTopMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM),
      paddingRightMm: scaled(legend.paddingRightMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM),
      paddingBottomMm: scaled(legend.paddingBottomMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM),
      paddingLeftMm: scaled(legend.paddingLeftMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM),
      groupSpacingMm: scaled(legend.groupSpacingMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM),
      itemSpacingMm: scaled(legend.itemSpacingMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM),
      bodyTypography: scaleTypography(legend.bodyTypography, scalar),
      heading: {
        ...legend.heading,
        typography: scaleTypography(legend.heading.typography, scalar),
      },
    },
    scaleBar: {
      ...composition.scaleBar,
      ...(composition.scaleBar.panel
        ? {
            panel: {
              ...composition.scaleBar.panel,
              cornerRadiusMm: scaled(composition.scaleBar.panel.cornerRadiusMm, 0, 50),
              paddingMm: scaled(composition.scaleBar.panel.paddingMm, 0, 50),
              border: {
                ...composition.scaleBar.panel.border,
                widthMm: scaled(composition.scaleBar.panel.border.widthMm, 0.1, 5),
              },
            },
          }
        : {}),
      maximumWidthMm: scaled(composition.scaleBar.maximumWidthMm, 10, 100),
      fontSizePt: scaled(composition.scaleBar.fontSizePt, COMPOSITION_TYPOGRAPHY_MIN_PT, 24),
      thicknessMm: scaled(
        composition.scaleBar.thicknessMm,
        COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM,
        COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM,
      ),
    },
    northArrow: {
      ...composition.northArrow,
      sizeMm: scaled(composition.northArrow.sizeMm, 5, 50),
    },
    sourcesAttribution: {
      ...composition.sourcesAttribution,
      typography: scaleTypography(
        composition.sourcesAttribution.typography,
        scalar,
        COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT,
        COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT,
      ),
    },
    logo: resizedLogo(composition.logo, oldPage, newPage, factors, true),
  };
}
